/**
 * Shared layout helpers and card primitives used across the Atomic Design
 * showcase sections. Not exported from the router — only consumed within
 * the /atomic-design page tree.
 */

import type { CSSProperties, ReactNode } from 'react'

// ─────────────────────────────────────────────────────────────────────────────
// Layout helpers
// ─────────────────────────────────────────────────────────────────────────────

export function PageShell({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '220px 1fr',
        minHeight: '100vh',
        maxWidth: 1440,
        margin: '0 auto',
        background: 'var(--bg)',
      }}
    >
      {children}
    </div>
  )
}

export function Toc() {
  const links: Array<{ id: string; label: string; stage: string; num: string }> = [
    { id: '#tokens', label: 'Tokens', stage: 'atoms', num: '00' },
    { id: '#atoms', label: 'Atoms', stage: 'atoms', num: '01' },
    { id: '#molecules', label: 'Molecules', stage: 'molecules', num: '02' },
    { id: '#organisms', label: 'Organisms', stage: 'organisms', num: '03' },
    { id: '#templates', label: 'Templates', stage: 'templates', num: '04' },
  ]

  const stageColor: Record<string, string> = {
    atoms: 'var(--atom)',
    molecules: 'var(--mol)',
    organisms: 'var(--org)',
    templates: 'var(--tmpl)',
    pages: 'var(--page)',
  }

  return (
    <aside
      style={{
        position: 'sticky',
        top: 0,
        alignSelf: 'start',
        height: '100vh',
        borderRight: '1px solid var(--border)',
        background: 'var(--surface)',
        padding: '24px 0',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          padding: '0 18px 14px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: 'var(--text-3)',
            fontWeight: 600,
          }}
        >
          Lumen · Library
        </div>
        <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.015em' }}>
          Atomic Design
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-2)' }}>
          Parity showcase · HR+
        </div>
      </div>

      <div>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'var(--text-3)',
            fontWeight: 600,
            padding: '8px 18px 4px',
          }}
        >
          Stages
        </div>
        {links.map((link) => (
          <a
            key={link.id}
            href={link.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '7px 18px',
              fontSize: 12.5,
              color: 'var(--text-2)',
              textDecoration: 'none',
              transition: 'background 0.12s, color 0.12s',
            }}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLAnchorElement).style.background = 'var(--surface-2)'
              ;(e.currentTarget as HTMLAnchorElement).style.color = 'var(--text)'
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLAnchorElement).style.background = 'transparent'
              ;(e.currentTarget as HTMLAnchorElement).style.color = 'var(--text-2)'
            }}
          >
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                color: 'var(--text-3)',
                width: 18,
                flexShrink: 0,
              }}
            >
              {link.num}
            </span>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                background: stageColor[link.stage] ?? 'var(--text-3)',
                flexShrink: 0,
              }}
            />
            {link.label}
          </a>
        ))}
      </div>
    </aside>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Card primitive
// ─────────────────────────────────────────────────────────────────────────────

export interface CardProps {
  name: string
  tag: string
  desc?: string
  children: ReactNode
  dark?: boolean
  col?: boolean
  tight?: boolean
  left?: boolean
  noPad?: boolean
  uses?: Array<{ stage: 'atom' | 'molecule' | 'organism'; label: string }>
}

export function PrimitiveCard({
  name,
  tag,
  desc,
  children,
  dark,
  col,
  tight,
  left,
  noPad,
  uses,
}: CardProps) {
  const previewStyle: CSSProperties = {
    padding: tight ? '14px' : noPad ? 0 : '24px 18px',
    flex: 1,
    display: 'flex',
    alignItems: col ? 'stretch' : left ? 'center' : 'center',
    justifyContent: left ? 'flex-start' : 'center',
    flexDirection: col ? 'column' : 'row',
    gap: col ? 10 : undefined,
    background: dark
      ? 'var(--space-cadet)'
      : `linear-gradient(0deg, var(--bg), var(--bg)), repeating-linear-gradient(45deg, transparent 0 6px, rgba(32,41,69,.025) 6px 7px)`,
    minHeight: tight ? 80 : 120,
    position: 'relative',
  }

  const useStageColor: Record<string, string> = {
    atom: 'rgba(0,72,204,.08)',
    molecule: 'rgba(0,130,203,.08)',
    organism: 'rgba(45,156,110,.1)',
  }
  const useStageText: Record<string, string> = {
    atom: 'var(--atom)',
    molecule: 'var(--mol)',
    organism: 'var(--org)',
  }

  return (
    <article
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          padding: '14px 16px 12px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            letterSpacing: '-0.005em',
            fontFamily: 'var(--font-mono)',
          }}
        >
          {name}
        </div>
        <div
          style={{
            marginLeft: 'auto',
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--text-3)',
            fontWeight: 600,
          }}
        >
          {tag}
        </div>
      </header>
      {desc ? (
        <div
          style={{
            padding: '10px 16px 0',
            fontSize: 12,
            color: 'var(--text-2)',
            lineHeight: 1.5,
          }}
          dangerouslySetInnerHTML={{ __html: desc }}
        />
      ) : null}
      <div style={previewStyle}>{children}</div>
      {uses && uses.length > 0 ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '8px 16px 14px',
            borderTop: '1px dashed var(--border)',
            fontSize: 10.5,
            fontFamily: 'var(--font-mono)',
          }}
        >
          <span
            style={{
              color: 'var(--text-3)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontWeight: 600,
              marginRight: 4,
              alignSelf: 'center',
            }}
          >
            Uses
          </span>
          {uses.map((u) => (
            <span
              key={u.label}
              style={{
                padding: '2px 6px',
                borderRadius: 4,
                background: useStageColor[u.stage] ?? 'var(--surface-2)',
                color: useStageText[u.stage] ?? 'var(--text-2)',
                fontWeight: 500,
              }}
            >
              {u.label}
            </span>
          ))}
        </div>
      ) : null}
    </article>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Grid helpers
// ─────────────────────────────────────────────────────────────────────────────

export function Grid2({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
      {children}
    </div>
  )
}

export function Grid3({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
      {children}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Section / group headers
// ─────────────────────────────────────────────────────────────────────────────

export function SectionHeader({
  num,
  name,
  tag,
  desc,
  stage,
}: {
  num: string
  name: string
  tag: string
  desc: string
  stage: string
}) {
  const stageColor: Record<string, string> = {
    atoms: 'var(--atom)',
    molecules: 'var(--mol)',
    organisms: 'var(--org)',
    templates: 'var(--tmpl)',
    pages: 'var(--page)',
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 18,
        paddingBottom: 18,
        borderBottom: '1px solid var(--border)',
        marginBottom: 28,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontWeight: 700,
          fontSize: 54,
          lineHeight: 0.9,
          letterSpacing: '-0.04em',
          color: stageColor[stage] ?? 'var(--text-3)',
        }}
      >
        {num}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 28,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
          }}
        >
          {name}
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              background: 'var(--surface-2)',
              color: 'var(--text-2)',
              padding: '3px 8px',
              borderRadius: 999,
              fontWeight: 600,
            }}
          >
            {tag}
          </span>
        </div>
        <div
          style={{
            fontSize: 14,
            color: 'var(--text-2)',
            marginTop: 6,
            maxWidth: '64ch',
            lineHeight: 1.55,
          }}
        >
          {desc}
        </div>
      </div>
    </div>
  )
}

export function GroupHeader({ label }: { label: string }) {
  return (
    <div
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10.5,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: 'var(--text-3)',
        fontWeight: 600,
        margin: '32px 0 12px',
        paddingLeft: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      {label}
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Token swatches
// ─────────────────────────────────────────────────────────────────────────────

export function Swatch({ color, name, hex }: { color: string; name: string; hex: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: 8,
        borderRadius: 9,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
      }}
    >
      <span
        style={{
          width: 34,
          height: 34,
          borderRadius: 7,
          flexShrink: 0,
          background: color,
          boxShadow: 'inset 0 0 0 1px rgba(32,41,69,.06)',
        }}
      />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 0, minWidth: 0 }}>
        <span style={{ fontSize: 12, fontWeight: 500, letterSpacing: '-0.005em' }}>{name}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-3)' }}>
          {hex}
        </span>
      </span>
    </div>
  )
}

export function SwatchGrid({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 8,
        width: '100%',
      }}
    >
      {children}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Template skeleton helpers
// ─────────────────────────────────────────────────────────────────────────────

export function TmplBar({ label }: { label: string }) {
  return (
    <div
      style={{
        height: 24,
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface-2)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '0 10px',
      }}
    >
      {[1, 2, 3].map((i) => (
        <span
          key={i}
          style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--border-strong)' }}
        />
      ))}
      <span
        style={{
          marginLeft: 8,
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </span>
    </div>
  )
}

type TmplBlockType = 'default' | 'fill' | 'accent' | 'dark'

export function TmplBlock({
  label,
  type = 'default',
  style,
}: {
  label: string
  type?: TmplBlockType
  style?: CSSProperties
}) {
  const bg: Record<TmplBlockType, string> = {
    default: 'var(--surface-2)',
    fill: `repeating-linear-gradient(45deg, var(--surface-2) 0 7px, var(--french-gray-3) 7px 8px)`,
    accent: 'var(--baby-blue-tint)',
    dark: 'var(--space-cadet)',
  }
  const fg: Record<TmplBlockType, string> = {
    default: 'var(--text-3)',
    fill: 'var(--text-3)',
    accent: 'var(--steel-blue-3)',
    dark: 'var(--pear)',
  }

  return (
    <div
      style={{
        background: bg[type],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--font-mono)',
        fontSize: 10.5,
        color: fg[type],
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        padding: 10,
        textAlign: 'center',
        ...style,
      }}
    >
      {label}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Calendar cell helper
// ─────────────────────────────────────────────────────────────────────────────

export function CalCell({
  day,
  variant,
  evt,
}: {
  day: number
  variant?: 'today' | 'holiday' | 'blackout'
  evt?: string
}) {
  const base: CSSProperties = {
    width: 78,
    height: 78,
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '6px 8px',
    fontSize: 11.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  }

  if (variant === 'today') {
    base.borderColor = 'var(--space-cadet)'
    base.borderWidth = 1.5
  } else if (variant === 'holiday') {
    base.background = 'var(--french-gray-2)'
    base.color = 'var(--payne)'
  } else if (variant === 'blackout') {
    base.background =
      'repeating-linear-gradient(135deg, rgba(194,138,45,.06) 0 8px, transparent 8px 16px)'
    base.boxShadow = 'inset 0 0 0 1.5px var(--warning)'
  }

  return (
    <div style={base}>
      <span style={{ fontWeight: 500, color: 'var(--text-2)' }}>{day}</span>
      {evt ? (
        <span
          style={{
            fontSize: 10,
            padding: '1px 5px',
            borderRadius: 4,
            background:
              variant === 'holiday'
                ? 'var(--french-gray)'
                : variant === 'blackout'
                  ? 'var(--space-cadet)'
                  : 'var(--baby-blue-tint)',
            color:
              variant === 'holiday'
                ? 'var(--payne)'
                : variant === 'blackout'
                  ? '#fff'
                  : 'var(--steel-blue-3)',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {evt}
        </span>
      ) : null}
    </div>
  )
}
